import "../chunk-WUKYLWAZ.mjs";
import {
  __privateAdd,
  __privateMethod
} from "../chunk-GV6AIHPN.mjs";

// src/solana/strategies/injected.ts
var MAX_RETRIES = 4;
var BASE_DELAY = 100;
var _getProvider, getProvider_fn;
var InjectedSolanaStrategy = class {
  constructor() {
    __privateAdd(this, _getProvider);
    this.type = "injected" /* INJECTED */;
  }
  load() {
    let retryCount = 0;
    const scheduleRetry = (resolve, reject) => {
      const delay = BASE_DELAY * Math.pow(2, Math.min(retryCount, 5));
      setTimeout(() => {
        if (__privateMethod(this, _getProvider, getProvider_fn).call(this)) {
          resolve();
          return;
        }
        retryCount++;
        if (retryCount >= MAX_RETRIES) {
          reject();
        } else {
          scheduleRetry(resolve, reject);
        }
      }, delay);
    };
    return new Promise((resolve, reject) => {
      scheduleRetry(() => resolve(this), reject);
    });
  }
  getProvider() {
    return __privateMethod(this, _getProvider, getProvider_fn).call(this) || null;
  }
  get isConnected() {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    return provider?.isConnected && provider.publicKey ? true : false;
  }
  async connect({ onlyIfTrusted }) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    if (provider.isConnected && provider.publicKey) {
      return this.getAccount() ?? void 0;
    }
    try {
      const result = await provider.connect({ onlyIfTrusted });
      return result.publicKey.toString();
    } catch (_) {
      return void 0;
    }
  }
  async disconnect() {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    await provider.disconnect();
  }
  async getAccount() {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (provider && provider.isConnected && provider.publicKey) {
      return Promise.resolve(provider.publicKey.toString());
    }
    return Promise.resolve(void 0);
  }
  async signMessage(message, display) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    if (!provider.isConnected) {
      throw new Error("Provider is not connected.");
    }
    const result = await provider.signMessage(message, display);
    return {
      signature: result.signature,
      address: result.publicKey.toString()
    };
  }
  async signIn(signInData) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    const result = await provider.signIn(signInData);
    return {
      address: result.address.toString(),
      signature: result.signature,
      signedMessage: result.signedMessage
    };
  }
  async signAndSendTransaction(transaction) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    if (!provider.isConnected) {
      throw new Error("Provider is not connected.");
    }
    const result = await provider.signAndSendTransaction(transaction);
    return {
      signature: result.signature,
      address: result.publicKey
    };
  }
  async signAndSendAllTransactions(transactions) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    if (!provider.isConnected) {
      throw new Error("Provider is not connected.");
    }
    if (!provider.signAndSendAllTransactions) {
      throw new Error("Provider does not support signAndSendAllTransactions.");
    }
    const results = await provider.signAndSendAllTransactions(transactions);
    return {
      signatures: results.signatures,
      address: results.publicKey
    };
  }
  async signTransaction(transaction) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    if (!provider.isConnected) {
      throw new Error("Provider is not connected.");
    }
    const result = await provider.signTransaction(transaction);
    return result;
  }
  async signAllTransactions(transactions) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    if (!provider.isConnected) {
      throw new Error("Provider is not connected.");
    }
    const result = await provider.signAllTransactions(transactions);
    return result;
  }
};
_getProvider = new WeakSet();
getProvider_fn = function() {
  if (typeof window === "undefined") {
    return void 0;
  }
  return window?.phantom?.solana;
};

// src/solana/getProvider.ts
async function getProvider(strategy = "injected" /* INJECTED */) {
  if (strategy === "injected") {
    const provider = new InjectedSolanaStrategy();
    try {
      await provider.load();
      return provider;
    } catch (error) {
      throw new Error("Provider not found.");
    }
  } else {
    throw new Error("Invalid provider type.");
  }
}

// src/solana/eventListeners.ts
var eventCallbacks = /* @__PURE__ */ new Map();
function addEventListener(event, callback) {
  if (!eventCallbacks.has(event)) {
    eventCallbacks.set(event, /* @__PURE__ */ new Set());
  }
  eventCallbacks.get(event)?.add(callback);
  return () => {
    removeEventListener(event, callback);
  };
}
function removeEventListener(event, callback) {
  if (eventCallbacks.has(event)) {
    eventCallbacks.get(event)?.delete(callback);
    if (eventCallbacks.get(event)?.size === 0) {
      eventCallbacks.delete(event);
    }
  }
}
function triggerEvent(event, ...args) {
  if (eventCallbacks.has(event)) {
    eventCallbacks.get(event)?.forEach((cb) => {
      try {
        cb(...args);
      } catch (error) {
        console.error(`Error in ${event} event listener:`, error);
      }
    });
  }
}

// src/solana/connect.ts
async function connect({ onlyIfTrusted = false } = {}) {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  if (provider.isConnected) {
    return provider.getAccount();
  }
  try {
    const address = await provider.connect({ onlyIfTrusted: true });
    if (address) {
      triggerEvent("connect", address);
      return address;
    }
  } catch (error) {
  }
  if (onlyIfTrusted) {
    throw new Error("No trusted connection available.");
  }
  try {
    const address = await provider.connect({ onlyIfTrusted: false });
    if (address) {
      triggerEvent("connect", address);
      return address;
    }
  } catch (error) {
  }
  throw new Error("Failed to connect to Phantom.");
}

// src/solana/disconnect.ts
async function disconnect() {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  await provider.disconnect();
  triggerEvent("disconnect");
}

// src/solana/getAccount.ts
async function getAccount() {
  const provider = await getProvider();
  return provider.getAccount();
}

// src/solana/signAndSendTransaction.ts
async function signAndSendTransaction(transaction) {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  if (!provider.isConnected) {
    await provider.connect({ onlyIfTrusted: false });
  }
  return provider.signAndSendTransaction(transaction);
}

// src/solana/signAndSendAllTransactions.ts
async function signAndSendAllTransactions(transactions) {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  if (!provider.isConnected) {
    await provider.connect({ onlyIfTrusted: false });
  }
  return provider.signAndSendAllTransactions(transactions);
}

// src/solana/signTransaction.ts
async function signTransaction(transaction) {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  if (!provider.isConnected) {
    await provider.connect({ onlyIfTrusted: false });
  }
  return provider.signTransaction(transaction);
}

// src/solana/signAllTransactions.ts
async function signAllTransactions(transactions) {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  if (!provider.isConnected) {
    await provider.connect({ onlyIfTrusted: false });
  }
  return provider.signAllTransactions(transactions);
}

// src/solana/signMessage.ts
async function signMessage(message, display) {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  if (!provider.isConnected) {
    await provider.connect({ onlyIfTrusted: false });
  }
  return provider.signMessage(message, display);
}

// src/solana/plugin.ts
var Solana = class {
  constructor() {
    this._publicKey = null;
    this.bindProviderEvents();
  }
  get publicKey() {
    return this._publicKey;
  }
  get connected() {
    return this._publicKey !== null;
  }
  async connect(options) {
    const address = await connect(options);
    if (!address) {
      throw new Error("Failed to connect to Solana wallet");
    }
    this._publicKey = address;
    return { publicKey: address };
  }
  async disconnect() {
    await disconnect();
    this._publicKey = null;
  }
  async signMessage(message) {
    const messageBytes = typeof message === "string" ? new TextEncoder().encode(message) : message;
    const result = await signMessage(messageBytes);
    return {
      signature: result.signature instanceof Uint8Array ? result.signature : new Uint8Array(result.signature),
      publicKey: result.address || this._publicKey || ""
    };
  }
  signTransaction(transaction) {
    return signTransaction(transaction);
  }
  async signAndSendTransaction(transaction) {
    const result = await signAndSendTransaction(transaction);
    return { signature: result.signature };
  }
  signAllTransactions(transactions) {
    return signAllTransactions(transactions);
  }
  async signAndSendAllTransactions(transactions) {
    const result = await signAndSendAllTransactions(transactions);
    return { signatures: result.signatures };
  }
  async switchNetwork(_network) {
    return Promise.resolve();
  }
  async getPublicKey() {
    if (this._publicKey) {
      return this._publicKey;
    }
    try {
      const account = await getAccount();
      this._publicKey = account || null;
      return this._publicKey;
    } catch {
      return null;
    }
  }
  isConnected() {
    return this._publicKey !== null;
  }
  on(event, listener) {
    addEventListener(event, listener);
  }
  off(event, listener) {
    removeEventListener(event, listener);
  }
  async bindProviderEvents() {
    try {
      const strategy = await getProvider();
      const provider = strategy.getProvider();
      if (provider) {
        provider.on("connect", (publicKey) => {
          if (publicKey) {
            const pubKey = publicKey.toString();
            this._publicKey = pubKey;
            triggerEvent("connect", pubKey);
          }
        });
        provider.on("disconnect", () => {
          this._publicKey = null;
          triggerEvent("disconnect");
        });
        provider.on("accountChanged", (publicKey) => {
          if (publicKey) {
            const pubKey = publicKey.toString();
            this._publicKey = pubKey;
            triggerEvent("accountChanged", pubKey);
            triggerEvent("connect", pubKey);
          } else {
            this._publicKey = null;
            triggerEvent("accountChanged", null);
          }
        });
      }
    } catch (error) {
    }
  }
};
function createSolanaPlugin() {
  return {
    name: "solana",
    create: () => {
      return new Solana();
    }
  };
}
export {
  createSolanaPlugin
};
